import * as THREE from 'three';
import * as util from '../util';

const EPS = 1e-7;

export function getExists(def) {
  for (var i = 1; i < arguments.length; i++) {
    def = def[arguments[i]];
    if (def === undefined) {
      return false;
    }
  }
  return true;
}

export function get(def) {
  if (def === undefined) {
    throw {
      jsonDefError: true,
      message: 'The window definition is invalid. Please contact admin.',
    };
  }
  for (var i = 1; i < arguments.length; i++) {
    def = def[arguments[i]];
    if (def === undefined) {
      throw {
        jsonDefError: true,
        message: arguments[i] + ' is undefined. Please contact admin.',
      };
    }
  }
  return def;
}

export function getOrNull(def) {
  for (
    var i = 1;
    i < arguments.length && def !== undefined && def !== null;
    i++
  ) {
    def = def[arguments[i]];
  }
  if (def === undefined || def === null) {
    return null;
  }
  return def;
}

export function getColorOrNull(def) {
  def = getOrNull.apply(this, arguments);
  if (!util.isHex(def)) {
    return null;
  }
  return util.hexToNumber(def);
}

export function getNumber(def) {
  def = get.apply(this, arguments);
  if (isNaN(def) || def === null) {
    throw {
      jsonDefError: true,
      message:
        arguments[arguments.length - 1] +
        ' is not a number. Please contact admin.',
    };
  }
  return Number(def);
}

export function getNumberOrNull(def) {
  def = getOrNull.apply(this, arguments);
  if (def === undefined || def === null || isNaN(def)) {
    return null;
  }
  return Number(def);
}

export function getInt(def) {
  def = getNumber.apply(this, arguments);
  if (def % 1 !== 0) {
    throw {
      jsonDefError: true,
      message:
        arguments[arguments.length - 1] +
        ' is not an integer. Please contact admin.',
    };
  }
  return def;
}

export function getBool(def) {
  def = get.apply(this, arguments);
  if (
    (typeof def === 'string' && def.toLowerCase() === 'true') ||
    def === true
  ) {
    return true;
  } else if (
    (typeof def === 'string' && def.toLowerCase() === 'false') ||
    def === false
  ) {
    return false;
  }
  throw {
    jsonDefError: true,
    message:
      arguments[arguments.length - 1] +
      ' is not a boolean. Please contact admin.',
  };
}

export function getBoolOrNull(def) {
  def = getOrNull.apply(this, arguments);
  if (def === undefined || def === null) {
    return null;
  }
  return getBool(def);
}

export function getObjectNumbersInThousands(def) {
  def = get.apply(this, arguments);
  const obj = Object.assign({}, def);
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      obj[key] = getNumber(obj[key]) / 1000;
    }
  }
  return obj;
}

export function getObjectNumbersInThousandsOrNull(def) {
  def = getOrNull.apply(this, arguments);
  if (!def) {
    return null;
  }
  const obj = Object.assign({}, def);
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      obj[key] = getNumber(obj[key]) / 1000;
    }
  }
  return obj;
}

export function sumObjects(o1) {
  if (o1 === undefined) {
    throw 'At least one object is required.';
  }

  const result = Object.assign({}, o1);
  for (let i = 1; i < arguments.length; i++) {
    const obj = arguments[i];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] += obj[key];
      }
    }
  }
  return result;
}

export function subtractObjects(o1) {
  if (o1 === undefined) {
    throw 'At least one object is required.';
  }

  const result = Object.assign({}, o1);
  for (let i = 1; i < arguments.length; i++) {
    const obj = arguments[i];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] -= obj[key];
      }
    }
  }
  return result;
}

export function multiplyObjectScalar(o1, scalar) {
  if (o1 === undefined) {
    throw 'At least one object is required.';
  }

  const result = Object.assign({}, o1);
  for (const key in result) {
    result[key] *= scalar;
  }
  return result;
}

export function buildCoupledFrame(def, d) {
  d.coupledFrameMaterial = new THREE.MeshPhongMaterial({
    color: util.hexToNumber(
      get(def, 'Coating', 'TCoatingSpecification', 'Key')
    ),
    shininess: 50,
    wireframe: false,
  });
}

export function rotateRectClockwise(dimensions, times) {
  if (times === 0) {
    return dimensions;
  }

  const rotated = {
    top: dimensions.left,
    left: dimensions.bottom,
    bottom: dimensions.right,
    right: dimensions.top,
  };
  return rotateRectClockwise(rotated, (((times % 4) + 4) % 4) - 1);
}

export function createConvexSolid2DGeometry(vertices2D, mirror) {
  const v = vertices2D;
  const g = new THREE.Geometry();
  for (let i = 0; i < v.length; i += 2) {
    g.vertices.push(new THREE.Vector3(v[i], v[i + 1], 0));
  }
  for (let i = 1; i < vertices2D.length / 2 - 1; i++) {
    g.faces.push(new THREE.Face3(0, i + 1, i));
  }
  if (mirror) {
    g.applyMatrix(new THREE.Matrix4().makeScale(-1, 1, 1));
  }
  return g;
}

export function createConvexSolidGeometry(
  verticesFront2D,
  verticesBack2D,
  breadth
) {
  const hb = breadth / 2; // Half breadth
  const vf = verticesFront2D; // Front vertices
  const vb = verticesBack2D; // Back vertices
  const vc = vb.length / 2; // Vertex count
  const g = new THREE.Geometry();
  const outsideFaces = [];

  // Add front vertices
  for (let i = 0; i < vf.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vf[i], vf[i + 1], -hb));
  }

  // Add back vertices
  for (let i = 0; i < vb.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vb[i], vb[i + 1], hb));
  }

  // Add front and back faces
  for (let i = 0; i < vc - 2; i++) {
    g.faces.push(new THREE.Face3(0, i + 1, i + 2)); // Front
    g.faces.push(new THREE.Face3(vc, vc + i + 2, vc + i + 1)); // Back
  }

  // Add side faces
  for (let i0 = 0; i0 < vc; i0++) {
    const i1 = (i0 + 1) % vc;
    outsideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(i0, vc + i0, i1));
    outsideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(i0 + vc, vc + i1, i1));
  }

  g.data = {
    outsideFaces: outsideFaces,
  };

  g.computeFaceNormals();
  return g;
}

export function createConvexBorderGeometry(
  verticesFrontInner2D,
  verticesFrontOuter2D,
  verticesBackInner2D,
  verticesBackOuter2D,
  breadth
) {
  const hb = breadth / 2;
  const vfi = verticesFrontInner2D;
  const vfo = verticesFrontOuter2D;
  const vbi = verticesBackInner2D;
  const vbo = verticesBackOuter2D;
  const vc = vbo.length / 2; // Vertex count
  const g = new THREE.Geometry();
  const insideFaces = [];
  const outsideFaces = [];

  // Add front inner vertices
  for (let i = 0; i < vfi.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vfi[i], vfi[i + 1], -hb));
  }

  // Add front outer vertices
  for (let i = 0; i < vfo.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vfo[i], vfo[i + 1], -hb));
  }

  // Add back inner vertices
  for (let i = 0; i < vbi.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vbi[i], vbi[i + 1], hb));
  }

  // Add back outer vertices
  for (let i = 0; i < vbo.length; i += 2) {
    g.vertices.push(new THREE.Vector3(vbo[i], vbo[i + 1], hb));
  }

  // Add faces
  for (let i0 = 0; i0 < vc; i0++) {
    const i1 = (i0 + 1) % vc;
    g.faces.push(new THREE.Face3(vc * 0 + i0, vc * 1 + i0, vc * 1 + i1)); // Front 1
    g.faces.push(new THREE.Face3(vc * 0 + i0, vc * 1 + i1, vc * 0 + i1)); // Front 2
    g.faces.push(new THREE.Face3(vc * 3 + i0, vc * 2 + i0, vc * 3 + i1)); // Back 1
    g.faces.push(new THREE.Face3(vc * 2 + i0, vc * 2 + i1, vc * 3 + i1)); // Back 2
    insideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(vc * 2 + i0, vc * 0 + i0, vc * 0 + i1)); // Inside 1
    insideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(vc * 2 + i0, vc * 0 + i1, vc * 2 + i1)); // Inside 2
    outsideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(vc * 1 + i0, vc * 3 + i0, vc * 1 + i1)); // Outside 1
    outsideFaces.push(g.faces.length);
    g.faces.push(new THREE.Face3(vc * 3 + i0, vc * 3 + i1, vc * 1 + i1)); // Outside 2
  }

  g.data = {
    insideFaces: insideFaces,
    outsideFaces: outsideFaces,
  };

  g.computeFaceNormals();
  return g;
}

export function setFrontMaterial(mesh, materialIndex) {
  for (let i = 0; i < mesh.geometry.faces.length; i++) {
    const f = mesh.geometry.faces[i];
    if (Math.abs(f.normal.z + 1) < EPS) {
      f.materialIndex = materialIndex;
    }
  }
}

export function setBackMaterial(mesh, materialIndex) {
  for (let i = 0; i < mesh.geometry.faces.length; i++) {
    const f = mesh.geometry.faces[i];
    if (Math.abs(f.normal.z - 1) < EPS) {
      f.materialIndex = materialIndex;
    }
  }
}

export function setInsideMaterial(mesh, materialIndex) {
  const g = mesh.geometry;
  const faceIndicies = g.data.insideFaces;
  for (let i = 0; i < faceIndicies.length; i++) {
    g.faces[faceIndicies[i]].materialIndex = materialIndex;
  }
}

export function setOutsideMaterial(mesh, materialIndex) {
  const g = mesh.geometry;
  const faceIndicies = g.data.outsideFaces;
  for (let i = 0; i < faceIndicies.length; i++) {
    g.faces[faceIndicies[i]].materialIndex = materialIndex;
  }
}

export function setMaterial(mesh, materialIndex) {
  for (const face of mesh.geometry.faces) {
    face.materialIndex = materialIndex;
  }
}

let currentMullionId = 1;
export function nextMullionId() {
  var id = currentMullionId++;
  return id;
}

export function resetMullionId() {
  currentMullionId = 1;
}

export function createVentilationFillGeometry(width, height, zPos) {
  const xSegments = 16;
  const ySegments = 8;
  const ew = width * 0.25; // Ellipsoid width radius
  const eh = ew * 0.8; // Ellipsoid height radius
  const eb = 0.01; // Ellipsoid breadth radius
  const margin = 0.05;
  const minSpacing = 0.03;

  const totalObjects = Math.floor((height - margin) / (minSpacing + eh));
  const spacing = (height - margin * 2 - totalObjects * eh) / totalObjects;

  // Ellipse definition: x^2 / a^2 + y^2 / b^2 = 1
  // https://en.wikipedia.org/wiki/Ellipse

  // Ellipsoid definition: x^2 / a^2 + y^2 / b^2 + z^2 / c^2 = 1
  // https://en.wikipedia.org/wiki/Ellipsoid

  const g = new THREE.Geometry();
  for (let i = 0; i < totalObjects; i++) {
    const baseY = height / 2 - margin - eh - (eh + spacing) * i;
    const fv = g.vertices.length;

    for (let ys = 0; ys < ySegments; ys++) {
      const y = eh - (ys / (ySegments - 1)) * eh;
      const xw = Math.sqrt(ew * ew * (1.0 - (y * y) / (eh * eh))); // Ellipse width radius at current y position
      for (let xs = 0; xs < xSegments; xs++) {
        const x = -xw + (xs / (xSegments - 1)) * xw * 2;
        // Math.max(0, ...) to prevent sqrt of negative numbers in case of inaccurate floating number
        const z = Math.sqrt(
          Math.max(
            0,
            eb * eb * (1.0 - (x * x) / (ew * ew) - (y * y) / (eh * eh))
          )
        );
        g.vertices.push(new THREE.Vector3(x, baseY + y, z + zPos + 0.0003)); // Adding 0.0003 to fix outline as it otherwise fades into the mesh behind
      }
    }

    for (let ys = 0; ys < ySegments - 1; ys++) {
      const si = ys * xSegments;
      const nsi = (ys + 1) * xSegments;
      for (let xs = 0; xs < xSegments - 1; xs++) {
        g.faces.push(
          new THREE.Face3(fv + si + xs + 1, fv + si + xs, fv + nsi + xs)
        );
        g.faces.push(
          new THREE.Face3(fv + si + xs + 1, fv + nsi + xs, fv + nsi + xs + 1)
        );
      }
    }
  }

  g.computeVertexNormals();
  return g;
}
